import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import SuccessAlert from "./../components/child/Alert/SuccessAlert";
import Loader from "./../components/child/Loder";
import AdminNotificationDropdown from "../components/Admin Notification/AdminNotificationDropdown";
import ThemeToggleButton from "../helper/ThemeToggleButton";
import GlobalColumnResizer from "../helper/GlobalColumnResizer";

const SESSION_KEYS = [
  "authToken",
  "name",
  "admin_id",
  "profile_picture",
  "user_role",
  "permissions",
];

const SESSION_HEARTBEAT_KEY = "sessionHeartbeat";
const HEARTBEAT_INTERVAL_MS = 2000;
const HEARTBEAT_TTL_MS = 5000;

const isAnotherTabActive = () => {
  const lastBeat = Number(localStorage.getItem(SESSION_HEARTBEAT_KEY) || 0);
  return Date.now() - lastBeat < HEARTBEAT_TTL_MS;
};

const clearAuthData = () => {
  SESSION_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });
  localStorage.removeItem("rememberMe");
  localStorage.removeItem("rememberedEmail");
  localStorage.removeItem("rememberedPassword");
  localStorage.removeItem(SESSION_HEARTBEAT_KEY);
};

const getAuthToken = () =>
  sessionStorage.getItem("authToken") || localStorage.getItem("authToken");

const MasterLayout = ({ children }) => {
  let [sidebarActive, seSidebarActive] = useState(false);
  let [mobileMenu, setMobileMenu] = useState(false);
  const [successMessage, setSuccessMessage] = useState(""); // Manage Success Message
  const [loading, setLoading] = useState(false);
  const location = useLocation(); // Hook to get the current route
  const [permissions, setPermissions] = useState([]);
  const [status, setStatus] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const navigate = useNavigate();
  const userRole =
    localStorage.getItem("user_role") === "super_admin"
      ? "Super Admin"
      : "Sub Admin";

  const role = localStorage.getItem("user_role") || "";
  const adminid = localStorage.getItem("admin_id");
  useEffect(() => {
    const fetchAdminData = async () => {
      if (!adminid) return;

      setLoading(false);
      try {
        const API_ENDPOINT = `${process.env.REACT_APP_BASE_URL}/admin/adminById/${adminid}`;
        const authToken = getAuthToken();

        const response = await fetch(API_ENDPOINT, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });

        if (!response.ok) throw new Error("Failed to fetch admin data");

        const data = await response.json();

        setStatus(data.result.status);
        if (data.result.deletedAt != null) {
          clearAuthData();

          setSuccessMessage(
            "You have been signed out because your profile has been deleted by the admin. Please contact the admin to sign in again."
          );
          setTimeout(() => {
            setLoading(false);
            setSuccessMessage("");
            navigate("/sign-in");
          }, 1000);
        }
        if (data.result.status === "inactive") {
          clearAuthData();

          setSuccessMessage(
            "You have been signed out because your profile has been deactivated by the admin. Please contact the admin to sign in again."
          );
          setTimeout(() => {
            setLoading(false);
            setSuccessMessage("");
            navigate("/sign-in");
          }, 1000);
        }
        let parsedPermissions = [];
        if (typeof data.result.permissions === "string") {
          parsedPermissions = JSON.parse(data.result.permissions);
        } else if (
          typeof data.result.permissions === "object" &&
          data.result.permissions !== null
        ) {
          parsedPermissions = data.result.permissions;
        }
        setPermissions(parsedPermissions);
        localStorage.setItem("permissions", JSON.stringify(parsedPermissions));
      } catch (err) {
        console.error("Error fetching admin data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, [adminid]);

  useEffect(() => {
    try {
      const storedPermissions = JSON.parse(
        localStorage.getItem("permissions") || "[]"
      );

      setPermissions(
        Array.isArray(storedPermissions) ? storedPermissions : []
      );
    } catch (err) {
      console.error("Error parsing permissions from localStorage:", err);
    }
  }, []);

  const filteredPermissions = (Array.isArray(permissions) ? permissions : []).filter(
    (perm) =>
      perm?.moduleName &&
      (perm.add || perm.edit || perm.view || perm.delete || perm.review)
  );

  const moduleIcons = {
    Dashboard: "solar:home-smile-angle-outline",
    "analytics-reporting": "mdi:chart-line",
    subadmin: "mdi:shield-account-outline",
    User: "flowbite:users-group-outline",
    cms: "octicon:info-24",
    faq: "mage:message-question-mark-round",
    "vault-questions": "mage:message-question-mark-round",
    preferences: "mdi:application-braces-outline",
    "discovery-config": "mdi:cogs",
    "compatibility-control": "mdi:heart-plus-outline",
    "match-control": "mdi:heart-plus-outline",
    "audit-history": "mdi:file-document-outline",
    "notification": "mdi:bell-outline",          // For general notifications
    "mynotification": "mdi:bell-ring-outline",
    "user-report": "mdi:account-file-outline",
    "feature-requests": 'mdi:lightbulb-on-outline',
    surveys: "mdi:clipboard-text-outline",
    giveaways: "mdi:gift-outline",
    "payments-transactions": 'mdi:cash-multiple',
    "subscriptions": "mdi:credit-card-clock-outline",
    "help-supports": "mdi:headset",
    "admin-settings": "material-symbols:settings",
    "events": "mdi:calendar-star-outline",

  };

  // Sidebar structure (founder ask 2026-08-27): the flat 20-item list was
  // impossible to scan; grouped sections give someone onboarding a mental
  // map. Grouping follows the job, not the code: watch the numbers, run
  // the members, tune matching, follow the money, talk to people, keep
  // the machine running.
  const SUPER_ADMIN_MENU = [
    {
      title: "Overview",
      items: [
        { path: "dashboard", label: "Dashboard", icon: "solar:home-smile-angle-outline" },
        { path: "analytics-reporting", label: "Analytics Reporting", icon: "mdi:chart-line" },
      ],
    },
    {
      title: "Members",
      items: [
        { path: "user", label: "Users", icon: "flowbite:users-group-outline" },
        { path: "silent-churn", label: "Silent Churn", icon: "mdi:account-clock-outline" },
        { path: "user-report", label: "User Reports", icon: "mdi:account-file-outline" },
        // Review only (2026-10-13, backend utils/dupeFlag).
        { path: "possible-duplicates", label: "Possible Duplicates", icon: "mdi:account-multiple-check-outline" },
        { path: "audit-history", label: "Audit History", icon: "mdi:file-document-outline" },
      ],
    },
    {
      title: "Compatibility",
      items: [
        { path: "vault-questions", label: "Vault Questions", icon: "mage:message-question-mark-round" },
        { path: "preferences", label: "Preferences", icon: "mdi:application-braces-outline" },
        { path: "discovery-config", label: "Compatibility Algorithm", icon: "mdi:cogs" },
        { path: "compatibility-control", label: "Introductions", icon: "mdi:heart-plus-outline" },
      ],
    },
    {
      title: "Revenue",
      items: [
        { path: "payments-transactions", label: "Payments and Transactions", icon: "mdi:cash-multiple" },
        { path: "subscriptions", label: "Subscriptions", icon: "mdi:credit-card-clock-outline" },
      ],
    },
    {
      title: "Messaging",
      items: [
        { path: "notification", label: "Notifications", icon: "mdi:bell-outline" },
        { path: "mynotification", label: "My Notifications", icon: "mdi:bell-ring-outline" },
      ],
    },
    {
      title: "Community",
      items: [
        { path: "events", label: "Curiouzz Social", icon: "mdi:calendar-star-outline" },
        { path: "feature-requests", label: "Feature Requests", icon: "mdi:lightbulb-on-outline" },
        { path: "surveys", label: "Surveys", icon: "mdi:clipboard-text-outline" },
        // Super admins only: this menu is never shown to sub admins, and the
        // server answers 403 to anyone else (2026-10-22).
        { path: "giveaways", label: "Giveaways", icon: "mdi:gift-outline" },
        { path: "help-supports", label: "Help and Support", icon: "mdi:headset" },
        { path: "curiouzz-qr", label: "Curiouzz QR", icon: "mdi:qrcode-scan" },
      ],
    },
    {
      title: "System",
      items: [
        { path: "subadmin", label: "Sub Admins", icon: "mdi:shield-account-outline" },
        { path: "admin-settings", label: "Settings", icon: "material-symbols:settings" },
        { path: "cms", label: "CMS", icon: "octicon:info-24" },
        { path: "faq", label: "FAQ", icon: "mage:message-question-mark-round" },
      ],
    },
  ];
  useEffect(() => {
    const remembered = localStorage.getItem("rememberMe") === "true";
    const sessionToken = sessionStorage.getItem("authToken");
    const localToken = localStorage.getItem("authToken");

    if (remembered) {
      if (!localToken) {
        navigate("/sign-in");
        return;
      }
    } else if (sessionToken) {
      // Valid session in this tab
    } else if (isAnotherTabActive()) {
      // New tab: another tab still has the session — redirect only, do not clear storage
      navigate("/sign-in");
      return;
    } else if (localToken) {
      // Browser was closed: stale session-only data
      clearAuthData();
      navigate("/sign-in");
      return;
    } else {
      navigate("/sign-in");
      return;
    }

    setSessionReady(true);

    const handleDropdownClick = (event) => {
      event.preventDefault();
      const clickedLink = event.currentTarget;
      const clickedDropdown = clickedLink.closest(".dropdown");

      if (!clickedDropdown) return;

      const isActive = clickedDropdown.classList.contains("open");

      // Close all dropdowns
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        dropdown.classList.remove("open");
        const submenu = dropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = "0px"; // Collapse submenu
        }
      });

      // Toggle the clicked dropdown
      if (!isActive) {
        clickedDropdown.classList.add("open");
        const submenu = clickedDropdown.querySelector(".sidebar-submenu");
        if (submenu) {
          submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
        }
      }
    };
    // Attach click event listeners to all dropdown triggers
    const dropdownTriggers = document.querySelectorAll(
      ".sidebar-menu .dropdown > a, .sidebar-menu .dropdown > Link"
    );

    dropdownTriggers.forEach((trigger) => {
      trigger.addEventListener("click", handleDropdownClick);
    });

    const openActiveDropdown = () => {
      const allDropdowns = document.querySelectorAll(".sidebar-menu .dropdown");
      allDropdowns.forEach((dropdown) => {
        const submenuLinks = dropdown.querySelectorAll(".sidebar-submenu li a");
        submenuLinks.forEach((link) => {
          if (
            link.getAttribute("href") === location.pathname ||
            link.getAttribute("to") === location.pathname
          ) {
            dropdown.classList.add("open");
            const submenu = dropdown.querySelector(".sidebar-submenu");
            if (submenu) {
              submenu.style.maxHeight = `${submenu.scrollHeight}px`; // Expand submenu
            }
          }
        });
      });
    };

    // Open the submenu that contains the active route
    openActiveDropdown();

    // Cleanup event listeners on unmount
    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener("click", handleDropdownClick);
      });
    };
  }, [location.pathname, navigate]);

  useEffect(() => {
    if (!sessionReady) return;

    const remembered = localStorage.getItem("rememberMe") === "true";
    if (remembered || !sessionStorage.getItem("authToken")) return;

    const updateHeartbeat = () => {
      localStorage.setItem(SESSION_HEARTBEAT_KEY, Date.now().toString());
    };

    updateHeartbeat();
    const interval = setInterval(updateHeartbeat, HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sessionReady]);

  let sidebarControl = () => {
    seSidebarActive(!sidebarActive);
  };

  let mobileMenuControl = () => {
    setMobileMenu(!mobileMenu);
  };
  const handleLogout = () => {
    setLoading(true);
    clearAuthData();
    setSuccessMessage("You have Sign out successfully!! Redirecting...");
    setTimeout(() => {
      setLoading(false);
      setSuccessMessage("");
      navigate("/sign-in");
    }, 3000);
  };

  if (loading) {
    //alert(loading);
    if (loading) return <Loader />;
  }

  if (!sessionReady) {
    return <Loader />;
  }

  const basePath = "/curioizz-admin";
  const path = "dashboard";
  const isActive = location.pathname === `/${path}`;

  //s alert(location.pathname.startsWith(`/${path}`));
  return (
    <section className={mobileMenu ? "overlay active" : "overlay "}>
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {/* sidebar */}
      <aside
        className={
          sidebarActive
            ? "sidebar active "
            : mobileMenu
              ? "sidebar sidebar-open"
              : "sidebar"
        }
      >
        <button
          onClick={mobileMenuControl}
          type="button"
          className="sidebar-close-btn"
        >
          <Icon icon="radix-icons:cross-2" />
        </button>
        <div>
          <Link
            to={isActive ? `/dashboard` : "/dashboard"}
            className="sidebar-logo"
            onClick={(e) => e.preventDefault()}
            style={{ cursor: "auto" }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo.svg`}
              alt="site logo"
              className="light-logo"
            />
            {/* Dark-mode logo (2026-08-27): the template referenced a
                logo-light.png that never existed, so dark mode showed no
                logo at all. logo-light.svg is the brand logo with the navy
                wordmark turned white; the pink/purple mark stays. */}
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/logo-light.svg`}
              alt="site logo"
              className="dark-logo"
            />
            <img
              src={`${process.env.PUBLIC_URL}/assets/images/favicon.png`}
              alt="site logo"
              className="logo-icon"
            />
          </Link>
        </div>
        <div className="sidebar-menu-area">
          <ul className="sidebar-menu">
            {role === "super_admin" ? (
              <>
                {SUPER_ADMIN_MENU.map((section) => (
                  <React.Fragment key={section.title}>
                    <li className="sidebar-menu-group-title">
                      {section.title}
                    </li>
                    {section.items.map(({ path, label, icon }) => {
                      const itemActive = location.pathname === `/${path}`;
                      return (
                        <li key={path}>
                          {itemActive ? (
                            <a href={`/${path}`} className="active-page">
                              <Icon icon={icon} className="menu-icon" />
                              <span>{label}</span>
                            </a>
                          ) : (
                            <Link to={`/${path}`}>
                              <Icon icon={icon} className="menu-icon" />
                              <span>{label}</span>
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <>
              <li>
                {location.pathname === "/dashboard" ? (
                  <a href={`/dashboard`} className="active-page">
                    <Icon
                      icon="solar:home-smile-angle-outline"
                      className="menu-icon"
                    />
                    <span>Dashboard</span>
                  </a>
                ) : (
                  <Link to={`/dashboard`} className="">
                    <Icon
                      icon="solar:home-smile-angle-outline"
                      className="menu-icon"
                    />
                    <span>Dashboard</span>
                  </Link>
                )}
              </li>
              {
              filteredPermissions.map((perm) => {
                const rawPath = perm.moduleName.toLowerCase().replace(/\s+/g, "-");
                const path = rawPath === "match-control" ? "compatibility-control" : rawPath;
                const isActive = location.pathname === `/${path}`;
                const label =
                  perm.moduleName === "Space Type"
                    ? "Storage Type"
                    : perm.moduleName === "preferences"
                      ? "Preferences"
                      : perm.moduleName === "vault-questions"
                        ? "Vault Questions"
                        : perm.moduleName === "discovery-config"
                          ? "Compatibility Algorithm"
                          : perm.moduleName === "Match Control" || perm.moduleName === "Compatibility Control"
                            ? "Compatibility Control"
                            : perm.moduleName === "audit-history"
                              ? "Audit History"
                              : perm.moduleName === "user-report"
                                ? "User Report"
                                : perm.moduleName === "notification"
                                  ? "Notification"
                                  : perm.moduleName === "mynotification"
                                    ? "My Notification"
                                    : perm.moduleName === "feature-requests"
                                      ? "Feature Requests"
                                      : perm.moduleName === "analytics-reporting"
                                        ? "Analytics Reporting"
                                        : perm.moduleName === "payments-transactions"
                                          ? "Payment and Transactions"
                                          : perm.moduleName === "subscriptions"
                                            ? "Subscriptions"
                                            : perm.moduleName === "help-supports"
                                              ? "Help And Supports"
                                                                                              : path === "admin-settings"
                                                  ? "Settings"
                                                  : perm.moduleName;
                const basePath = "/curioizz-admin";
                return (
                  <li key={perm.moduleName}>
                    {isActive ? (
                        // <a href={`${basePath}/${path}`} className="active-page">
                      <a href={`/${path}`} className="active-page">

                        <Icon
                          icon={
                            moduleIcons[path] || "flowbite:users-group-outline"
                          }
                          className="menu-icon"
                        />
                        <span>{label}</span>
                      </a>
                    ) : (
                      <Link to={`/${path}`}>
                        <Icon
                          icon={
                            moduleIcons[path] || "flowbite:users-group-outline"
                          }
                          className="menu-icon"
                        />
                        <span>{label}</span>
                      </Link>
                    )}
                  </li>
                );
              })}
              </>
            )}
          </ul>
        </div>
      </aside>

      <main
        className={sidebarActive ? "dashboard-main active" : "dashboard-main"}
      >
        <div className="navbar-header">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-4">
                <button
                  type="button"
                  className="sidebar-toggle"
                  onClick={sidebarControl}
                >
                  {sidebarActive ? (
                    <Icon
                      icon="iconoir:arrow-right"
                      className="icon text-2xl non-active"
                    />
                  ) : (
                    <Icon
                      icon="heroicons:bars-3-solid"
                      className="icon text-2xl non-active "
                    />
                  )}
                </button>
                <button
                  onClick={mobileMenuControl}
                  type="button"
                  className="sidebar-mobile-toggle"
                >
                  <Icon icon="heroicons:bars-3-solid" className="icon" />
                </button>
              </div>
            </div>
            <div className="col-auto">
              <div className="d-flex flex-wrap align-items-center gap-3">

                <GlobalColumnResizer />
                <ThemeToggleButton />

                <AdminNotificationDropdown />

                {/* Notification dropdown end */}
                <div className="dropdown">
                  <button
                    className="d-flex justify-content-center align-items-center rounded-circle"
                    type="button"
                    data-bs-toggle="dropdown"
                  >
                    <img
                      src={localStorage.getItem("profile_picture")}
                      alt="image_user"
                      className="w-40-px h-40-px object-fit-cover rounded-circle"
                    />
                  </button>
                  <div className="dropdown-menu to-top dropdown-menu-sm">
                    <div className="py-12 px-16 radius-8 bg-primary-50 mb-16 d-flex align-items-center justify-content-between gap-2">
                      <div>
                        <h6 className="text-lg text-primary-light fw-semibold mb-2">
                          {localStorage.getItem("name")}
                        </h6>
                        <span className="text-secondary-light fw-medium text-sm">
                          {userRole}
                        </span>
                      </div>
                      <button type="button" className="hover-text-danger">
                        <Icon
                          icon="radix-icons:cross-1"
                          className="icon text-xl"
                        />
                      </button>
                    </div>
                    <ul className="to-top-list">
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-primary d-flex align-items-center gap-3"
                          to="/view-profile"
                        >
                          <Icon
                            icon="solar:user-linear"
                            className="icon text-xl"
                          />{" "}
                          My Profile
                        </Link>
                      </li>
                      <li>
                        <Link
                          className="dropdown-item text-black px-0 py-8 hover-bg-transparent hover-text-danger d-flex align-items-center gap-3"
                          to="#"
                          onClick={handleLogout}
                        >
                          <Icon icon="lucide:power" className="icon text-xl" />{" "}
                          Sign Out
                        </Link>
                      </li>
                    </ul>
                  </div>
                </div>
                {/* Profile dropdown end */}
              </div>
            </div>
          </div>
        </div>

        {/* dashboard-main-body */}
        <div className="dashboard-main-body">{children}</div>

        {/* Footer section */}
        <footer className="d-footer">
          <div className="row align-items-center justify-content-between">
            <div className="col-auto">
              <p className="mb-0">© {new Date().getFullYear()} Curiouzz</p>
            </div>
            <div className="col-auto">
              <p className="mb-0">
                Developed by{" "}
                <a
                  href="https://www.iqlance.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="text-primary-600">iQlance Solution</span>
                </a>
              </p>
            </div>
          </div>
        </footer>
      </main>
    </section>
  );
};

export default MasterLayout;
